import { isEmpty, isNil, maxBy, orderBy, sumBy } from 'lodash';
import compact from 'lodash/compact';

import { isFixedChargeLvoForUnit, isProvisionedChargeLvo, isProvisionedChargeLvoForUnit } from '../chargesUtils';
import {
  addCalendarDate,
  CalendarDate,
  formatCalendarDate,
  fromCalendarDate,
  Interval,
  isAfter,
  isBefore,
  isEqual,
  MIN_DATE,
  subtractCalendarDate,
  toCalendarDate,
  toCalendarDateNullable,
} from '../dateutils';
import { getUnitLeasesFromUnitOrDTO } from '../leaseutils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty, removeTypenameDeep } from '../objectUtils';
import { applyProRata, applyProRataWithTimeInterval } from '../periodUtils';
import {
  AccountLabel,
  Building,
  Charge,
  ChargeDTO,
  Contact,
  CreatePostingInput,
  Language,
  LeaseStatus,
  LeaseVariousOperation,
  LeaseVariousOperationDTO,
  LooseObject,
  Posting,
  PostingChargeType,
  PostingDTO,
  PostingStatus,
  PostingType,
  SettlementPartKind,
  Transaction,
  TransactionLinkType,
  Unit,
  UnitDTO,
  UnitLease,
  UnitLeaseDTO,
  VatRate,
} from '../types';

import { ACCOUNT_CONTRACTOR_TOPCLASS } from './accountClasses';
import { getPostingAmount } from './invoiceHelper';
import { getExcludedVatAmountFromIncludedVatAmount } from './vatutils';

export interface HandleAffectChargeOperationsBundle {
  createPosting: (input: CreatePostingInput | Omit<CreatePostingInput, 'clientId' | 'readId'>) => Promise<PostingDTO>;
  getUnit: (id: string) => Unit | undefined;
  getBuilding: (id: string) => Building | undefined;
  getAccountLabelForContactOrInsert: (
    contractor: Contact,
    ownerId?: string | null
  ) => Promise<AccountLabel | undefined>;
}

export interface AmountSplittedBetweenLease {
  unitLease?: UnitLease | UnitLeaseDTO;
  amount: number;
  periodTo: CalendarDate;
  periodFrom: CalendarDate;
}

type PostingPeriodSortOrder = 'asc' | 'desc';

export const orderPostingsByPeriodFrom = <T extends Pick<Posting, 'periodFrom' | 'periodTo'>>(
  postings: T[],
  sortOrder: PostingPeriodSortOrder = 'asc'
): T[] => orderBy(postings, ['periodFrom', 'periodTo'], [sortOrder, sortOrder]);

export const getChargePayableAmount = (
  charge: Pick<Charge, 'payableAmount' | 'totalAmount'>
): number | null | undefined => charge.payableAmount ?? charge.totalAmount;

export const getChargeOrderExecuteOn = (charge: Pick<Charge, 'settlement'>): string | null =>
  charge.settlement?.parts.find(({ kind }) => kind === SettlementPartKind.ORDER)?.executeOn ?? null;

// The share of the payment allocated to the supplier is the outgoing movement, so it is the opposite
// of the charge total; a payment split across several accounts only settles with its supplier share
export const isChargeSettledBySupplierPayment = (
  charge: Pick<Charge, 'contractorId' | 'payableAmount' | 'remittanceInformation' | 'totalAmount'>,
  payment: Pick<Transaction, 'links' | 'remittanceInformation'>
): boolean => {
  const payableAmount = getChargePayableAmount(charge);
  if (isNil(payableAmount) || isNilOrEmpty(charge.remittanceInformation) || isNil(charge.contractorId)) {
    return false;
  }
  const amountLinkedToSupplier = roundAtSecondDecimal(
    (payment.links ?? []).reduce(
      (total, link) =>
        link.linkType === TransactionLinkType.CONTACT && link.linkId === charge.contractorId
          ? total + link.amount
          : total,
      0
    )
  );
  return (
    amountLinkedToSupplier !== 0 &&
    payment.remittanceInformation === charge.remittanceInformation &&
    amountLinkedToSupplier === roundAtSecondDecimal(-payableAmount)
  );
};

/**
 * This function splits the amount between the leases of the unit
 * and its periods of vacancy by applying a pro rata between them
 * (based on the number of days).
 */
export const splitAmountBetweenUnitLeasesForPeriod = (
  periodInterval: Interval,
  unit: Unit | UnitDTO,
  amount: number
): AmountSplittedBetweenLease[] => {
  const splittedAmountsToReturn: AmountSplittedBetweenLease[] = [];
  /* Split the amount between all the lease depending on the overlapping days
   between the lease duration and the periodInterval */
  const unitLeases = getUnitLeasesFromUnitOrDTO(unit);
  for (const unitLease of unitLeases) {
    const lease = unitLease.lease;
    if (lease && (lease.status === LeaseStatus.Active || lease.status === LeaseStatus.Ended)) {
      const leaseStartDate = toCalendarDate(lease.startDate);
      const leaseEndDate = toCalendarDate(lease.endDate);
      const leaseInterval = { start: leaseStartDate, end: leaseEndDate };
      const proRatedAmount = applyProRataWithTimeInterval(periodInterval, leaseInterval, amount);
      if (proRatedAmount !== 0) {
        splittedAmountsToReturn.push({
          amount: proRatedAmount,
          unitLease,
          periodFrom: isBefore(leaseStartDate, periodInterval.start) ? periodInterval.start : leaseStartDate,
          periodTo: isAfter(leaseEndDate, periodInterval.end) ? periodInterval.end : leaseEndDate,
        });
      }
    }
  }
  if (isEmpty(splittedAmountsToReturn)) {
    const vacancyAmount = applyProRataWithTimeInterval(periodInterval, periodInterval, amount);
    return correctEventualRoundingMistakes(amount, [
      { amount: vacancyAmount, periodFrom: periodInterval.start, periodTo: periodInterval.end },
    ]);
  }
  // Sort the split by periodFrom, so they are in chronological order
  splittedAmountsToReturn.sort((s1, s2) => {
    return isBefore(s1.periodFrom, s2.periodFrom) ? -1 : 1;
  });
  /* Each period in the interval not covered correspond to a rent vacancy */
  const splittedVacancyPeriods: AmountSplittedBetweenLease[] = [];
  let nextUncoveredDate = periodInterval.start;
  for (const split of splittedAmountsToReturn) {
    const { periodFrom, periodTo } = split;

    if (isAfter(periodFrom, nextUncoveredDate)) {
      const vacancyPeriodTo = subtractCalendarDate(periodFrom, 1, 'days');
      const proRatedAmount = applyProRataWithTimeInterval(
        periodInterval,
        { start: nextUncoveredDate, end: vacancyPeriodTo },
        amount
      );
      splittedVacancyPeriods.push({
        amount: proRatedAmount,
        periodFrom: nextUncoveredDate,
        periodTo: vacancyPeriodTo,
      });
    }

    nextUncoveredDate = addCalendarDate(periodTo, 1, 'days');
  }
  // Finally is there a gap between the last covered date and the end of the interval ?
  if (!isAfter(nextUncoveredDate, periodInterval.end)) {
    const vacancyInterval = { start: nextUncoveredDate, end: periodInterval.end };
    const proRatedAmount = applyProRataWithTimeInterval(periodInterval, vacancyInterval, amount);
    splittedVacancyPeriods.push({
      amount: proRatedAmount,
      periodFrom: vacancyInterval.start,
      periodTo: vacancyInterval.end,
    });
  }

  const splitSortedByPeriodFrom = [...splittedAmountsToReturn, ...splittedVacancyPeriods].sort((s1, s2) =>
    isBefore(s1.periodFrom, s2.periodFrom) ? -1 : 1
  );

  return correctEventualRoundingMistakes(amount, splitSortedByPeriodFrom);
};

/**
 * This function takes a split in argument and the initial amount that was split.
 * If the sum of the split amounts is not equal to the initial amount, the difference will be
 * added on the highest split.
 */
const correctEventualRoundingMistakes = (initialAmount: number, splittedAmounts: AmountSplittedBetweenLease[]) => {
  const coveredAmount = sumBy(splittedAmounts, 'amount');
  const unCoveredAmount = roundAtSecondDecimal(initialAmount - coveredAmount);
  if (unCoveredAmount !== 0) {
    const highestSplit = maxBy(splittedAmounts, 'amount');
    if (highestSplit) {
      highestSplit.amount = roundAtSecondDecimal(highestSplit.amount + unCoveredAmount);
    }
  }
  return splittedAmounts;
};

/**
 * This function will split the amount between all the leases covered in the time window.
 * For each lease, the amount is pro-rated by the period covered of the lease and then divided between
 * the owner and the tenants according to the given owner/tenant repartition.
 *
 * In case of rental vacancy, the pro-rated amount is assigned exclusively to the owner (a custom posting is created
 * for this purpose)
 */
export const splitUnitRepartitionPartIntoPostings = async (
  amountToSplit: number,
  completeUnit: Unit,
  building: Building,
  accountLabel: AccountLabel,
  ownerId: string,
  periodFrom: string,
  periodTo: string,
  charge: Charge,
  splitNumber: number,
  vatRate: Pick<VatRate, 'id' | 'value'> | undefined | null,
  ownerRepartition: number,
  repartitionMetric: string,
  repartitionValue: number,
  leaseIdsUnpaidByTenant: string[] | undefined,
  createPosting: (input: CreatePostingInput | Omit<CreatePostingInput, 'clientId' | 'readId'>) => Promise<PostingDTO>,
  leaseVariousOperations: (LeaseVariousOperation | LeaseVariousOperationDTO)[] = []
) => {
  if (completeUnit) {
    const createPostingsPromises: Promise<PostingDTO>[] = [];
    const splittedAmountsBetweenLeases = splitAmountBetweenUnitLeasesForPeriod(
      { start: toCalendarDate(periodFrom), end: toCalendarDate(periodTo) },
      completeUnit,
      amountToSplit
    );
    splittedAmountsBetweenLeases.forEach((leaseSplittedAmount) => {
      const { unitLease, amount, periodFrom: postingPeriodFrom, periodTo: postingPeriodTo } = leaseSplittedAmount;
      const absAmount = Math.abs(amount);
      const repartitionValueProRated = applyProRata(repartitionValue, absAmount, amountToSplit);
      const vacancyPeriod = isNil(unitLease);
      if (vacancyPeriod) {
        // Vacancy period => Create one VACANCY posting and assign it to the owner
        const vacancyPostingStatus = charge.paid ? PostingStatus.PAID_BY_OWNER : PostingStatus.TO_BE_PAID_BY_OWNER;
        const amountVatExcluded = getExcludedVatAmountFromIncludedVatAmount(absAmount, vatRate?.value);
        const vatAmount = roundAtSecondDecimal(absAmount - amountVatExcluded);
        createPostingsPromises.push(
          createPosting({
            class: accountLabel.class,
            topClass: accountLabel.topClass,
            totalAmount: absAmount,
            amountVatExcluded,
            vatAmount,
            vatRateValue: vatRate?.value,
            vatRateId: vatRate?.id,
            periodFrom: fromCalendarDate(postingPeriodFrom),
            periodTo: fromCalendarDate(postingPeriodTo),
            buildingId: building.id,
            unitId: completeUnit.id,
            chargeId: charge.id,
            accountLabelId: accountLabel.id,
            contactId: ownerId, // on Owner !
            type: amount > 0 ? PostingType.DEBIT : PostingType.CREDIT,
            chargeSplitNumber: splitNumber,
            chargeOwnerRepartition: ownerRepartition,
            chargeType: PostingChargeType.OWNER_VACANCY, // Vacancy posting
            chargeRepartitionMetric: repartitionMetric,
            chargeRepartitionValue: repartitionValueProRated,
            status: vacancyPostingStatus,
          })
        );
      } else {
        // Amount is splitted between tenant and owner,
        // create the tenant posting and the owner posting if they have amounts > 0
        // Manual override means that the user flagged this lease to be paid by only the owner
        const manualOverride = !isNil(leaseIdsUnpaidByTenant) && leaseIdsUnpaidByTenant.includes(unitLease!.lease!.id);
        // We use the ownerRepartition if the lease was not flagged by the user. Otherwise 100% for the owner.
        const realOwnerRepartition = manualOverride ? 100 : ownerRepartition;
        const { tenantAmount, ownerAmount } = splitAmountBetweenTenantsAndOwner(amount, realOwnerRepartition);
        const { tenantAmount: tenantRepartitionValue, ownerAmount: ownerRepartitionValue } =
          splitAmountBetweenTenantsAndOwner(repartitionValueProRated, realOwnerRepartition);
        if (ownerAmount !== 0) {
          const absOwnerAmount = Math.abs(ownerAmount);
          // The owner posting
          const ownerAmountVatExcluded = getExcludedVatAmountFromIncludedVatAmount(absOwnerAmount, vatRate?.value);
          const ownerVatAmount = roundAtSecondDecimal(absOwnerAmount - ownerAmountVatExcluded);
          createPostingsPromises.push(
            createPosting({
              class: accountLabel.class,
              topClass: accountLabel.topClass,
              amountVatExcluded: ownerAmountVatExcluded,
              vatAmount: ownerVatAmount,
              vatRateValue: vatRate?.value,
              vatRateId: vatRate?.id,
              totalAmount: absOwnerAmount,
              periodFrom: fromCalendarDate(postingPeriodFrom),
              periodTo: fromCalendarDate(postingPeriodTo),
              buildingId: building.id,
              unitId: completeUnit.id,
              chargeId: charge.id,
              accountLabelId: accountLabel.id,
              contactId: ownerId, // on Owner !
              leaseId: unitLease!.lease!.id, // And on lease !
              type: ownerAmount > 0 ? PostingType.DEBIT : PostingType.CREDIT,
              chargeSplitNumber: splitNumber,
              chargeType: PostingChargeType.OWNER, // Owner posting
              chargeRepartitionMetric: repartitionMetric,
              chargeRepartitionValue: Math.abs(ownerRepartitionValue),
              chargeOwnerRepartition: realOwnerRepartition,
              status: charge.paid ? PostingStatus.PAID_BY_OWNER : PostingStatus.TO_BE_PAID_BY_OWNER,
            })
          );
        }
        if (tenantAmount !== 0) {
          const absTenantAmount = Math.abs(tenantAmount);
          // The tenant posting. Only make the tenant pay if the lease is in provision.
          const hasFixedChargeForUnit = hasFixedChargeLvoForUnit(leaseVariousOperations, unitLease!.unitId);
          const tenantPostingStatus = getTenantPostingStatus(hasFixedChargeForUnit, charge.paid);
          const tenantAmountVatExcluded = getExcludedVatAmountFromIncludedVatAmount(absTenantAmount, vatRate?.value);
          const tenantVatAmount = roundAtSecondDecimal(absTenantAmount - tenantAmountVatExcluded);
          createPostingsPromises.push(
            createPosting({
              class: accountLabel.class,
              topClass: accountLabel.topClass,
              amountVatExcluded: tenantAmountVatExcluded,
              vatAmount: tenantVatAmount,
              vatRateValue: vatRate?.value,
              vatRateId: vatRate?.id,
              totalAmount: absTenantAmount,
              periodFrom: fromCalendarDate(postingPeriodFrom),
              periodTo: fromCalendarDate(postingPeriodTo),
              buildingId: building.id,
              unitId: completeUnit.id,
              chargeId: charge.id,
              accountLabelId: accountLabel.id,
              contactId: ownerId,
              leaseId: unitLease!.lease!.id, // On lease !
              type: tenantAmount > 0 ? PostingType.DEBIT : PostingType.CREDIT,
              chargeSplitNumber: splitNumber,
              chargeType: PostingChargeType.TENANT, // Tenant posting
              chargeRepartitionMetric: repartitionMetric,
              chargeRepartitionValue: Math.abs(tenantRepartitionValue),
              chargeOwnerRepartition: realOwnerRepartition,
              status: tenantPostingStatus,
            })
          );
        }
      }
    });
    await Promise.all(createPostingsPromises);
  }
};

export const splitAmountBetweenTenantsAndOwner = (amountToSplit: number, ownerRepartition: number) => {
  const absAmountToSplit = Math.abs(amountToSplit);
  const ownerAmount = roundAtSecondDecimal((absAmountToSplit * ownerRepartition) / 100);
  const tenantAmount = roundAtSecondDecimal(absAmountToSplit - ownerAmount);
  if (amountToSplit < 0) {
    return { ownerAmount: -ownerAmount, tenantAmount: -tenantAmount };
  }
  return { ownerAmount, tenantAmount };
};

export const getTenantAmountFromOwnerAmount = (ownerAmount: number, ownerRepartition: number) => {
  if (ownerRepartition === 0) return ownerAmount;
  const tenantRepartition = 100 - ownerRepartition;
  return roundAtSecondDecimal((ownerAmount / ownerRepartition) * tenantRepartition);
};

export const getTenantAmountFromVacancyAmount = (totalVacancyAmount: number, ownerRepartition: number) => {
  if (ownerRepartition === 0) return totalVacancyAmount;
  const tenantRepartition = 100 - ownerRepartition;
  return roundAtSecondDecimal((totalVacancyAmount * tenantRepartition) / 100);
};

export const getOwnerAmountFromTenantAmount = (tenantAmount: number, ownerRepartition: number) => {
  if (ownerRepartition === 100) return tenantAmount;
  const tenantRepartition = 100 - ownerRepartition;
  return roundAtSecondDecimal((tenantAmount / tenantRepartition) * ownerRepartition);
};

export const linkChargeToSupplierAccount = async (
  periodFrom: string,
  periodTo: string,
  charge: Charge,
  operations: HandleAffectChargeOperationsBundle,
  ownerId?: string | null
) => {
  const { createPosting, getAccountLabelForContactOrInsert } = operations;

  const supplierAccountLabel = await getAccountLabelForContactOrInsert(charge.contractor!, ownerId);
  await createPosting({
    class: supplierAccountLabel?.class ?? ACCOUNT_CONTRACTOR_TOPCLASS,
    topClass: ACCOUNT_CONTRACTOR_TOPCLASS,
    totalAmount: Math.abs(charge.totalAmount!),
    periodFrom: periodFrom,
    periodTo: periodTo,
    accountLabelId: supplierAccountLabel?.id,
    chargeId: charge.id,
    contactId: charge.contractorId,
    type: charge.totalAmount! > 0 ? PostingType.CREDIT : PostingType.DEBIT,
  });
};

export const getOwnerIdOfUnit = (unit: Unit, getBuilding: (id: string) => Building | undefined) => {
  if (unit.jointOwnershipId) {
    return unit.jointOwnershipId;
  }
  if (unit.owners?.[0]?.owner) {
    return unit.owners[0].owner.id;
  }

  const completeBuilding = getBuilding(unit.buildingId);
  if (completeBuilding?.jointOwnershipId) {
    return completeBuilding.jointOwnershipId;
  }
  if (completeBuilding?.owners?.[0]?.owner) {
    return completeBuilding.owners[0].owner.id;
  }

  return undefined;
};

/**
 * The postings before the interval are treated at 100%
 * The postings overlapping with the inveral are pro rated accordingly
 * The postings after the interval are ignored
 */
export const isV2TenantLandingRow = (posting: Pick<Posting, 'chargeType' | 'tenantShare'>): boolean =>
  posting.chargeType === PostingChargeType.TENANT && !isNil(posting.tenantShare);

export const chargePostingAmountOnPeriod = (
  posting: PostingDTO | Posting,
  periodTo: CalendarDate,
  periodFrom = MIN_DATE
) => {
  if (isV2TenantLandingRow(posting)) {
    return posting.totalAmount!;
  }
  const intervalBeforeAndInStatement = { start: periodFrom, end: periodTo };
  const postingInterval = { start: toCalendarDate(posting.periodFrom), end: toCalendarDate(posting.periodTo) };
  const amountBeforeAndInInterval = applyProRataWithTimeInterval(
    postingInterval,
    intervalBeforeAndInStatement,
    posting.totalAmount!
  );
  // We have ignored the posting amount after the interval
  return amountBeforeAndInInterval;
};

export const getProRatedPostingsAmountOnPeriod = (
  postings: Posting[],
  periodFrom: CalendarDate,
  periodTo: CalendarDate
): number =>
  roundAtSecondDecimal(
    postings.reduce(
      (accumulator, posting) =>
        accumulator + getPostingAmount(chargePostingAmountOnPeriod(posting, periodTo, periodFrom), posting.type),
      0
    )
  );

/**
 * This function will mutate the postings in the list in such a way that
 * it will update their "amount" and "periodTo" variables so that they are
 * not considered after the statement end date.
 */
export const applyTimeProRataOnPostings = (postings: Posting[], periodTo: CalendarDate, periodFrom?: CalendarDate) =>
  postings.map((posting) => {
    if (isV2TenantLandingRow(posting)) {
      return posting;
    }
    const postingPeriodTo = toCalendarDate(posting.periodTo);
    const newPeriodTo = isBefore(periodTo, postingPeriodTo) ? periodTo : postingPeriodTo;
    const newProRatedAmount: number = chargePostingAmountOnPeriod(posting, newPeriodTo, periodFrom);
    const proRatedRepartitionValue = isNil(posting.chargeRepartitionValue)
      ? posting.chargeRepartitionValue
      : applyProRata(posting.chargeRepartitionValue, newProRatedAmount, posting.totalAmount);
    let amountVatExcluded = undefined;
    let vatAmount = undefined;
    if (!isNil(posting.amountVatExcluded) && !isNil(posting.vatRateValue) && !isNil(posting.vatAmount)) {
      amountVatExcluded = getExcludedVatAmountFromIncludedVatAmount(newProRatedAmount, posting.vatRateValue);
      vatAmount = roundAtSecondDecimal(newProRatedAmount - amountVatExcluded);
    }
    return {
      ...posting,
      periodTo: fromCalendarDate(newPeriodTo),
      totalAmount: newProRatedAmount,
      amountVatExcluded,
      vatAmount,
      chargeRepartitionValue: proRatedRepartitionValue,
    };
  });

export const copyPostingWithNewValues = (posting: PostingDTO, newValues: Partial<PostingDTO>) => {
  const {
    id: _id,
    building: _building,
    unit: _unit,
    invoice: _invoice,
    charge: _charge,
    transaction: _transaction,
    lease: _lease,
    bankAccount: _bankAccount,
    accountLabel: _accountLabel,
    contact: _contact,
    leaseVariousOperation: _leaseVariousOperation,
    _deleted,
    _version,
    type,
    __typename,
    _lastChangedAt,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    vatRate: _vatRate,
    ...infosToCopy
  } = posting;
  const newPosting = {};
  // DynamoDb throws error if we put null value in GSI variables !
  for (const [key, value] of Object.entries(infosToCopy)) {
    if (!isNil(value)) {
      (newPosting as LooseObject)[key] = value;
    }
  }
  return removeTypenameDeep({
    ...newPosting,
    type: type as PostingType,
    ...newValues,
  }) as CreatePostingInput;
};

/**
 * This function regroups postings that were splitted due to a lease extension/revocation so that we are showing
 * only one of them in the front end.
 *
 * When extending/revocating a lease, it can happen that we must split a posting into two contigous ones.
 * This is due to the fact that we must rebalance all leases charges that are not completely covered by a statement yet.
 * Indeed, if a period was vacant before and is now leased, we must assign the correct charges amount to the new lease.
 * But as explained in 'handlePostingsAlreadyIncludedInAStatement' in leaseUpdateNotificator it can happen that the
 * tenant part is more advanced that the owner part in term of statemetn (and vice-versa). So we were forced to split
 * a posting in two so the owner and tenant postings match in term of split. This allows to apply a correct
 * rebalance on the same postings that are not covered by a statement.
 * BUT these split should stay hidden from the user,
 * and only one posting should be shown to the user, not all its split!
 */
export const regroupAllSplittedPostings = (postings: Posting[]) => {
  const postingsSortedByPeriodFrom = orderPostingsByPeriodFrom(postings, 'asc');
  const [tenantPostings, ownerPostings, otherPosting] = postingsSortedByPeriodFrom.reduce(
    (acc: [Posting[], Posting[], Posting[]], posting) => {
      if (posting.status === PostingStatus.TO_BE_PAID_BY_TENANT) {
        acc[0].push(posting);
      } else if (posting.status === PostingStatus.TO_BE_PAID_BY_OWNER) {
        acc[1].push(posting);
      } else {
        acc[2].push(posting);
      }
      return acc;
    },
    [[], [], []]
  );
  const regroupedTenantPostings = regroupSplittedPostings(tenantPostings);
  const regroupedOwnerPostings = regroupSplittedPostings(ownerPostings);
  return [...regroupedTenantPostings, ...regroupedOwnerPostings, ...otherPosting];
};

const regroupSplittedPostings = (postings: Posting[]) => {
  return postings.reduce((acc: Posting[], posting) => {
    if (isEmpty(acc)) {
      acc.push(posting);
    } else {
      const previousPostingIndex = acc.length - 1;
      const previousPosting = acc[previousPostingIndex];
      const currentPostingIsASplitOfPreviousPosting = arePostingsASplit(previousPosting, posting);
      if (currentPostingIsASplitOfPreviousPosting) {
        // The posting was splitted, adapt previous posting to add the coverage of current one
        acc[previousPostingIndex] = {
          ...previousPosting,
          periodTo: posting.periodTo,
          totalAmount: roundAtSecondDecimal(previousPosting.totalAmount + posting.totalAmount),
          vatAmount:
            !isNil(previousPosting.vatAmount) && !isNil(posting.vatAmount)
              ? roundAtSecondDecimal(previousPosting.vatAmount + posting.vatAmount)
              : undefined,
          amountVatExcluded:
            !isNil(previousPosting.amountVatExcluded) && !isNil(posting.amountVatExcluded)
              ? roundAtSecondDecimal(previousPosting.amountVatExcluded + posting.amountVatExcluded)
              : undefined,
          chargeRepartitionValue:
            !isNil(previousPosting.chargeRepartitionValue) && !isNil(posting.chargeRepartitionValue)
              ? roundAtSecondDecimal(previousPosting.chargeRepartitionValue + posting.chargeRepartitionValue)
              : undefined,
        };
      } else {
        // It is not a splitted posting, nothing to do excpect adding the posting to the list
        acc.push(posting);
      }
    }
    return acc;
  }, []);
};

// p1 should be should start before p2
const arePostingsASplit = (p1: Posting, p2: Posting): boolean => {
  const haveSamePostingIdentity =
    p1.chargeId === p2.chargeId &&
    p1.accountLabelId === p2.accountLabelId &&
    p1.vatRateId === p2.vatRateId &&
    p1.chargeSplitNumber === p2.chargeSplitNumber &&
    p1.buildingId === p2.buildingId &&
    p1.unitId === p2.unitId &&
    (p1.leaseId === p2.leaseId || (isNil(p1.leaseId) && isNil(p2.leaseId)));

  if (!haveSamePostingIdentity) {
    return false;
  }

  const firstPeriodTo = toCalendarDate(p1.periodTo);
  const secondPeriodFrom = toCalendarDate(p2.periodFrom);

  return isEqual(firstPeriodTo, subtractCalendarDate(secondPeriodFrom, 1, 'days'));
};

export const getChargePostings = (charge: Charge | ChargeDTO): Array<Posting | PostingDTO> => {
  const rawPostings = charge.postings;
  if (Array.isArray(rawPostings)) {
    return rawPostings;
  }
  return rawPostings?.items?.filter((p): p is PostingDTO => p !== null) ?? [];
};

export const getFixedChargeLvosForUnit = (
  lvoItems: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  unitId: string
): (LeaseVariousOperation | LeaseVariousOperationDTO)[] =>
  lvoItems.filter((lvo) => isFixedChargeLvoForUnit(lvo, unitId));

export const getProvisionedChargeLvosForUnit = (
  lvoItems: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  unitId: string
): (LeaseVariousOperation | LeaseVariousOperationDTO)[] =>
  lvoItems.filter((lvo) => isProvisionedChargeLvoForUnit(lvo, unitId));

export const hasFixedChargeLvoForUnit = (
  lvoItems: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  unitId: string
): boolean => lvoItems.some((lvo) => isFixedChargeLvoForUnit(lvo, unitId));

export const hasProvisionedChargeLvoForUnit = (
  lvoItems: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  unitId: string
): boolean => lvoItems.some((lvo) => isProvisionedChargeLvoForUnit(lvo, unitId));

export const hasProvisionedChargeLvo = (
  lvoItems: (LeaseVariousOperation | LeaseVariousOperationDTO | null | undefined)[]
): boolean => lvoItems.some((lvo) => !isNil(lvo) && isProvisionedChargeLvo(lvo));

export const getTenantPostingStatus = (hasFixedChargeForUnit: boolean, paid: boolean): PostingStatus => {
  if (!hasFixedChargeForUnit) return PostingStatus.TO_BE_PAID_BY_TENANT;
  return paid ? PostingStatus.PAID_BY_OWNER : PostingStatus.TO_BE_PAID_BY_OWNER;
};

/** Parts of the label of a charge in a list: its supplier and its invoice date, when known. */
export const getChargeLabelParts = (
  charge: Pick<Charge, 'invoiceDate'>,
  contractorName: string | undefined,
  language: Language
): string[] => {
  const invoiceDate = toCalendarDateNullable(charge.invoiceDate);
  return compact([contractorName, isNil(invoiceDate) ? undefined : formatCalendarDate(invoiceDate, language)]);
};
